import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Check, ChefHat, HeartPulse, Home, Salad, PlayCircle } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { FadeUp } from "../components/site/motion";
import { DemoNote } from "../components/site/Badges";
import { Container, SectionHead } from "../components/sections/SectionHead";
import { M } from "../data/site";

const TRACKS = [
  { icon: ChefHat, t: "Kitchen Foundations", d: "Knife skills, batch prep and weeknight confidence.", n: 8 },
  { icon: Salad, t: "Nutrition Made Simple", d: "Read labels, build balanced plates, shop smarter.", n: 6 },
  { icon: HeartPulse, t: "Wellness & Routines", d: "Sleep, movement and calmer family evenings.", n: 7 },
  { icon: Home, t: "Everyday Life Skills", d: "Budgeting, planning and running a home together.", n: 5 },
];
const MODULES = ["Why a balanced plate matters", "The half-plate vegetable rule", "Smart protein choices", "Whole grains without the fuss", "Putting it together: a week of plates"];

export default function University() {
  const [done, setDone] = useState([0]);
  const [active, setActive] = useState(1);
  return (
    <div data-testid="university-page">
      <PageHero eyebrow="HonestGrow™ University" img={M("university.jpg")} alt="Honesti teaching a group of adults in a warm living room"
        lines={[{ text: "Small lessons." }, { text: "Healthier homes.", className: "text-grad" }]}
        intro="Short, practical courses for parents and caregivers — guided by Honesti — covering cooking, nutrition, wellness, routines and everyday life.">
        <Link to="/waitlist" data-testid="university-waitlist-btn" className="btn-primary mt-8">Get early access <ArrowRight className="h-4 w-4" /></Link>
      </PageHero>
      <Container testId="university-tracks">
        <SectionHead eyebrow="Sample course catalog" title={<>Four tracks, <span className="text-grad">one calmer home</span></>} sub="Illustrative curriculum — final courses may differ." />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TRACKS.map((t, i) => (
            <FadeUp key={t.t} delay={i * 0.08} className="h-full">
              <div className="glass group h-full rounded-2xl p-6 transition-transform duration-500 hover:-translate-y-2" data-testid={`track-${i}`}>
                <t.icon className="h-9 w-9 text-cyan-200" strokeWidth={1.3} />
                <h3 className="mt-6 font-display text-2xl font-semibold text-white">{t.t}</h3>
                <p className="mt-2 text-sm text-slate-300">{t.d}</p>
                <p className="mt-6 flex items-center gap-2 font-mono text-xs text-cyan-300"><BookOpen className="h-3.5 w-3.5" />{t.n} LESSONS</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </Container>
      <section className="mx-auto max-w-[1440px] px-5 pb-24 lg:px-12">
        <FadeUp>
          <div className="glass grid gap-8 rounded-[2rem] p-6 lg:grid-cols-12 lg:p-12">
            <div className="lg:col-span-5">
              <p className="eyebrow">Sample lesson · Nutrition Made Simple</p>
              <h2 className="mt-4 font-display text-3xl font-bold text-white lg:text-4xl">Building a balanced family plate</h2>
              <div className="mt-6 h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full bg-gradient-to-r from-cyan-300 to-blue-500 transition-[width] duration-500" style={{ width: `${(done.length / MODULES.length) * 100}%` }} /></div>
              <p className="mt-2 text-xs text-slate-300" data-testid="lesson-progress">{done.length} of {MODULES.length} modules complete (demo)</p>
              <div className="mt-6"><DemoNote>Demonstration only. Progress is not saved and lessons are placeholders.</DemoNote></div>
            </div>
            <ol className="space-y-2 lg:col-span-7">
              {MODULES.map((m, i) => (
                <li key={m}>
                  <button onClick={() => { setActive(i); setDone((d) => (d.includes(i) ? d : [...d, i])); }} data-testid={`lesson-module-${i}`}
                    className={`flex w-full items-center gap-4 rounded-xl px-4 py-4 text-left transition-colors ${active === i ? "bg-cyan-400/15" : "hover:bg-white/5"}`}>
                    <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full ${done.includes(i) ? "bg-cyan-400 text-ink" : "border border-cyan-400/40 text-cyan-200"}`}>{done.includes(i) ? <Check className="h-4 w-4" /> : <PlayCircle className="h-4 w-4" />}</span>
                    <span className="flex-1 text-white">{m}</span><span className="font-mono text-xs text-slate-300">{4 + i} min</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
